import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ChevronRight, ChevronLeft, CheckCircle, XCircle, AlertCircle, Users, Home, Shield, Heart, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Volunteer Guidance Tips per Section
const VOLUNTEER_GUIDANCE = {
  1: {
    title: 'Warm Opening',
    tips: [
      '"I\'m glad you reached out — let\'s take this one step at a time."',
      'Listen without interruption and acknowledge any stress the neighbor is experiencing.',
      'Use calm, steady language. Avoid rushing or making assumptions.',
      'Never promise assistance during intake. Say: "We\'re gathering information so our team can understand your situation."'
    ]
  },
  2: {
    title: 'Understanding the Household',
    tips: [
      'Neighbors may be navigating difficult changes — benefit reductions, rising costs, job loss, illness, or caregiving.',
      'Approach every conversation with patience, empathy, and respect.',
      'If no recent hardship is identified, consider coaching or deeper discernment.',
      'Focus especially on families with children, seniors, disabled members, and those who struggle to communicate their situation.'
    ]
  },
  3: {
    title: 'Documentation & Housing',
    tips: [
      'Checks can only be made payable to the landlord as recorded on a current lease agreement.',
      'If documents are missing, assist the neighbor in gathering them rather than turning them away.',
      'If they\'ve received other rental assistance, coordinate with those agencies to avoid duplication.',
      'If the landlord refuses third-party payment or has not completed a W-9, rental assistance cannot proceed.'
    ]
  },
  4: {
    title: 'Stewardship & Sustainability',
    tips: [
      'Ask: will this assistance truly solve the problem, or only provide temporary relief without a path forward?',
      'Example: Paying $2,000 for a household unable to pay next month may not stabilize housing.',
      'Ensure limited resources reach those with the greatest need.',
      'Being attentive to sustainability is an act of stewardship, not judgment.'
    ]
  },
  5: {
    title: 'The Heart of the Work',
    tips: [
      'A small team reviews each case for effectiveness, compassion, stewardship, and mission alignment.',
      'Home visits are a long-standing Vincentian practice — they help confirm stated needs and may reveal vulnerabilities not shared during intake.',
      'Our goal: listen well, respond with compassion, steward resources responsibly.',
      'Every decision should uphold the dignity of every person served and work to prevent homelessness.'
    ]
  }
};

// Section 1: Contact & Eligibility
const ELIGIBILITY_QUESTIONS = [
  {
    id: 'q1',
    question: "Has the individual contacted SVDP directly or been referred?",
    noOutcome: { type: 'refer', message: 'Provide referral or alternate support.' }
  },
  {
    id: 'q2',
    question: "Do they live within parish boundaries?",
    noOutcome: { type: 'refer', message: 'Provide referral or alternate support.' }
  },
  {
    id: 'q3',
    question: "Are they currently housed (not experiencing homelessness/unhoused)?",
    noOutcome: { type: 'refer', message: 'Provide referral or alternate support.' }
  }
];

// Section 3: Housing Situation
const HOUSING_QUESTIONS = [
  {
    id: 'q4',
    question: "Behind on rent or facing eviction?",
    noNote: 'Explore other forms of support (utilities, food, etc.).'
  },
  {
    id: 'q5',
    question: "Can they provide documentation (lease, ID, notice) in their name?",
    noNote: 'Assist with gathering documents or offer provisional support.'
  },
  {
    id: 'q6',
    question: "Have they received other rental assistance?",
    yesNote: 'Check for duplication and coordinate with other agencies.'
  },
  {
    id: 'q7',
    question: "Is the landlord willing to accept third-party payment?",
    noOutcome: { type: 'ineligible', message: 'Rental assistance cannot proceed — landlord will not accept third-party payment.' }
  },
  {
    id: 'q8',
    question: "Has the landlord completed a W-9?",
    noOutcome: { type: 'action', message: 'Request W-9 from landlord; assistance cannot proceed without it. Checks can only be made payable to the landlord as recorded on a current lease agreement.' }
  }
];

// Section 4: Stability Assessment
const STABILITY_CHECKS = [
  { id: 'prevent_eviction', label: 'Will the assistance prevent eviction?' },
  { id: 'stabilize_housing', label: 'Will it stabilize housing beyond this month? (rent not exceeding ~30% of income)' },
  { id: 'realistic_given_income', label: 'Is it realistic given income and household size?' },
  { id: 'protect_vulnerable', label: 'Will it protect vulnerable members (children, seniors, disabled)?' }
];

const SECTION_LABELS = [
  { title: 'Contact & Eligibility', icon: Shield },
  { title: 'Household Assessment', icon: Users },
  { title: 'Housing Situation', icon: Home },
  { title: 'Stability Assessment', icon: CheckCircle },
  { title: 'Pastoral Discernment', icon: Heart }
];

const DecisionTreeWizard = ({ caseId, neighbor, onClose, onComplete }) => {
  // Current section (1-5) and step within section
  const [currentSection, setCurrentSection] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [outcome, setOutcome] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showGuidance, setShowGuidance] = useState(true);

  // Section 2: Household Assessment form data
  const [householdData, setHouseholdData] = useState({
    adults: '',
    children: '',
    seniors: '',
    disabled_members: '',
    marital_status: '',
    number_of_income_sources: '',
    total_household_size: '',
    recent_hardship: ''
  });

  // Section 4: Stability checks
  const [stabilityChecks, setStabilityChecks] = useState({
    prevent_eviction: null,
    stabilize_housing: null,
    realistic_given_income: null,
    protect_vulnerable: null
  });

  // Section 5: Pastoral review
  const [pastoralNotes, setPastoralNotes] = useState('');

  const totalSteps = ELIGIBILITY_QUESTIONS.length + 1 + HOUSING_QUESTIONS.length + 1 + 1; // 11 total
  const getCompletedSteps = () => {
    let count = 0;
    ELIGIBILITY_QUESTIONS.forEach(q => { if (responses[q.id]) count++; });
    if (currentSection > 2 || (currentSection === 2 && householdData.adults !== '')) count++;
    HOUSING_QUESTIONS.forEach(q => { if (responses[q.id]) count++; });
    if (currentSection > 4) count++;
    if (currentSection > 5 || outcome) count++;
    return count;
  };

  const handleYesNo = (questionId, answer, questionSet) => {
    const newResponses = { ...responses, [questionId]: answer };
    setResponses(newResponses);
    setHistory([...history, { section: currentSection, questionIndex: currentQuestionIndex }]);

    const question = questionSet.find(q => q.id === questionId);

    if (answer === 'no' && question.noOutcome) {
      setOutcome(question.noOutcome);
      return;
    }

    if (answer === 'yes' && question.yesNote) {
      // Note but continue
    }

    // Move to next question or next section
    if (questionSet === ELIGIBILITY_QUESTIONS) {
      if (currentQuestionIndex < ELIGIBILITY_QUESTIONS.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setCurrentSection(2);
        setCurrentQuestionIndex(0);
      }
    } else if (questionSet === HOUSING_QUESTIONS) {
      if (currentQuestionIndex < HOUSING_QUESTIONS.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setCurrentSection(4);
        setCurrentQuestionIndex(0);
      }
    }
  };

  const handleHouseholdSubmit = () => {
    if (!householdData.adults && !householdData.total_household_size) {
      toast.error('Please fill in at least the number of adults or total household size');
      return;
    }
    setResponses({
      ...responses,
      household_data: householdData
    });
    setHistory([...history, { section: 2, questionIndex: 0 }]);
    setCurrentSection(3);
    setCurrentQuestionIndex(0);
  };

  const handleStabilitySubmit = () => {
    const allChecked = Object.values(stabilityChecks).every(v => v !== null);
    if (!allChecked) {
      toast.error('Please answer all stability assessment questions');
      return;
    }
    const allYes = Object.values(stabilityChecks).every(v => v === true);
    setResponses({
      ...responses,
      stability_checks: stabilityChecks
    });
    setHistory([...history, { section: 4, questionIndex: 0 }]);

    if (!allYes) {
      setOutcome({ type: 'action', message: 'Not all stability conditions are met. Consider wraparound support or alternative aid.' });
      return;
    }
    setCurrentSection(5);
    setCurrentQuestionIndex(0);
  };

  const handlePastoralDecision = (decision) => {
    setResponses({
      ...responses,
      pastoral_review: { decision, notes: pastoralNotes }
    });

    if (decision === 'approve') {
      setOutcome({ type: 'approve', message: 'APPROVED — Recommend for assistance. Case reviewed for effectiveness, compassion, stewardship, mission alignment, and dignity of housing.' });
    } else {
      setOutcome({ type: 'refer', message: 'REFERRED — Recommend additional support planning. Case reviewed for effectiveness, compassion, stewardship, and alignment with SVDP mission.' });
    }
  };

  const handleBack = () => {
    if (outcome) {
      setOutcome(null);
      return;
    }
    if (history.length > 0) {
      const newHistory = [...history];
      const prev = newHistory.pop();
      setHistory(newHistory);
      setCurrentSection(prev.section);
      setCurrentQuestionIndex(prev.questionIndex);

      // Clean up responses for the step we're going back from
      const newResponses = { ...responses };
      if (prev.section === 1) {
        delete newResponses[ELIGIBILITY_QUESTIONS[prev.questionIndex]?.id];
      } else if (prev.section === 2) {
        delete newResponses.household_data;
      } else if (prev.section === 3) {
        delete newResponses[HOUSING_QUESTIONS[prev.questionIndex]?.id];
      } else if (prev.section === 4) {
        delete newResponses.stability_checks;
      }
      setResponses(newResponses);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      let finalOutcome = 'NEEDS_ACTION';
      if (outcome?.type === 'approve') finalOutcome = 'APPROVE';
      else if (outcome?.type === 'refer') finalOutcome = 'REFER';
      else if (outcome?.type === 'ineligible') finalOutcome = 'INELIGIBLE';

      await axios.post(`${API}/decision-tree`, {
        case_id: caseId,
        responses: responses,
        final_outcome: finalOutcome,
        outcome_reason: outcome?.message || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Decision tree completed');
      onComplete && onComplete();
      onClose();
    } catch (error) {
      console.error('Error saving decision tree:', error);
      toast.error('Failed to save decision tree');
    } finally {
      setSaving(false);
    }
  };

  const getOutcomeIcon = (type) => {
    switch (type) {
      case 'approve': return <CheckCircle className="w-16 h-16 text-emerald-400" />;
      case 'ineligible': return <XCircle className="w-16 h-16 text-rose-400" />;
      default: return <AlertCircle className="w-16 h-16 text-amber-400" />;
    }
  };

  const getOutcomeColor = (type) => {
    switch (type) {
      case 'approve': return 'bg-emerald-500/10 border-emerald-500/30';
      case 'ineligible': return 'bg-rose-500/10 border-rose-500/30';
      default: return 'bg-amber-500/10 border-amber-500/30';
    }
  };

  const getOutcomeTitle = (type) => {
    switch (type) {
      case 'approve': return 'Eligible for Assistance';
      case 'ineligible': return 'Not Eligible';
      case 'refer': return 'Referral Needed';
      default: return 'Action Required';
    }
  };

  // Render section progress indicator
  const renderSectionProgress = () => (
    <div className="px-6 pt-4">
      <div className="flex items-center justify-between mb-3">
        {SECTION_LABELS.map((section, idx) => {
          const sectionNum = idx + 1;
          const isActive = currentSection === sectionNum;
          const isCompleted = currentSection > sectionNum;
          const Icon = section.icon;
          return (
            <div key={idx} className="flex flex-col items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                isCompleted ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                isActive ? 'bg-[#0072BC]/20 text-[#0072BC] border border-[#0072BC]/50' :
                'bg-white/5 text-slate-500 border border-white/10'
              }`}>
                {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <p className={`text-[10px] mt-1 text-center leading-tight hidden sm:block ${
                isActive ? 'text-white' : 'text-slate-500'
              }`}>{section.title}</p>
            </div>
          );
        })}
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#0072BC] transition-all duration-500"
          style={{ width: `${(getCompletedSteps() / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );

  // Render a yes/no question
  const renderYesNoQuestion = (question, questionSet) => (
    <div data-testid={`decision-tree-question-${question.id}`}>
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-[#0072BC]/20 text-[#0072BC] rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 border border-[#0072BC]/30">
          {question.id.replace('q', '')}
        </div>
        <h3 className="text-xl font-semibold text-white">
          {question.question}
        </h3>
        {question.noNote && responses[question.id] === 'no' && (
          <p className="text-sm text-amber-400 mt-2">{question.noNote}</p>
        )}
        {question.yesNote && responses[question.id] === 'yes' && (
          <p className="text-sm text-amber-400 mt-2">{question.yesNote}</p>
        )}
      </div>
      <div className="flex gap-4 justify-center">
        <button
          onClick={() => handleYesNo(question.id, 'yes', questionSet)}
          className="flex-1 max-w-[200px] py-4 px-6 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 font-semibold rounded-xl transition-colors text-lg border border-emerald-500/30"
          data-testid={`decision-tree-yes-btn`}
        >
          Yes
        </button>
        <button
          onClick={() => handleYesNo(question.id, 'no', questionSet)}
          className="flex-1 max-w-[200px] py-4 px-6 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 font-semibold rounded-xl transition-colors text-lg border border-rose-500/30"
          data-testid={`decision-tree-no-btn`}
        >
          No
        </button>
      </div>
    </div>
  );

  // Render Household Assessment form (Section 2)
  const renderHouseholdForm = () => (
    <div data-testid="household-assessment-form">
      <div className="text-center mb-6">
        <Users className="w-10 h-10 text-[#0072BC] mx-auto mb-2" />
        <h3 className="text-xl font-semibold text-white">Household Assessment</h3>
        <p className="text-sm text-slate-400 mt-1">Collect household size, composition, and income information</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Adults</label>
          <input
            type="number" min="0"
            value={householdData.adults}
            onChange={(e) => setHouseholdData({...householdData, adults: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
            placeholder="0"
            data-testid="household-adults"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Children</label>
          <input
            type="number" min="0"
            value={householdData.children}
            onChange={(e) => setHouseholdData({...householdData, children: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
            placeholder="0"
            data-testid="household-children"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Seniors</label>
          <input
            type="number" min="0"
            value={householdData.seniors}
            onChange={(e) => setHouseholdData({...householdData, seniors: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
            placeholder="0"
            data-testid="household-seniors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Disabled Members</label>
          <input
            type="number" min="0"
            value={householdData.disabled_members}
            onChange={(e) => setHouseholdData({...householdData, disabled_members: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
            placeholder="0"
            data-testid="household-disabled"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Marital Status</label>
          <select
            value={householdData.marital_status}
            onChange={(e) => setHouseholdData({...householdData, marital_status: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none appearance-none"
            data-testid="household-marital-status"
          >
            <option value="" className="bg-slate-800">Select...</option>
            <option value="Single" className="bg-slate-800">Single</option>
            <option value="Married" className="bg-slate-800">Married</option>
            <option value="Divorced" className="bg-slate-800">Divorced</option>
            <option value="Widowed" className="bg-slate-800">Widowed</option>
            <option value="Separated" className="bg-slate-800">Separated</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Income Sources</label>
          <select
            value={householdData.number_of_income_sources}
            onChange={(e) => setHouseholdData({...householdData, number_of_income_sources: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none appearance-none"
            data-testid="household-income-sources"
          >
            <option value="" className="bg-slate-800">Select...</option>
            <option value="0" className="bg-slate-800">None</option>
            <option value="1" className="bg-slate-800">One income</option>
            <option value="2" className="bg-slate-800">Two incomes</option>
            <option value="3+" className="bg-slate-800">Three or more</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-slate-300 mb-1">Total People in Household</label>
          <input
            type="number" min="1"
            value={householdData.total_household_size}
            onChange={(e) => setHouseholdData({...householdData, total_household_size: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none"
            placeholder="Total number of people living in the house"
            data-testid="household-total-size"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-slate-300 mb-1">Recent Hardship</label>
          <textarea
            value={householdData.recent_hardship}
            onChange={(e) => setHouseholdData({...householdData, recent_hardship: e.target.value})}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none resize-none"
            rows={3}
            placeholder="Job loss, illness, reduced hours, caregiving, etc."
            data-testid="household-hardship"
          />
          <p className="text-xs text-slate-500 mt-1">If no hardship: consider coaching or deeper discernment.</p>
        </div>
      </div>
      <div className="mt-6 flex justify-center">
        <Button
          onClick={handleHouseholdSubmit}
          className="bg-[#0072BC] hover:bg-[#005a96] px-8"
          data-testid="household-continue-btn"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // Render Stability Assessment (Section 4)
  const renderStabilityAssessment = () => (
    <div data-testid="stability-assessment">
      <div className="text-center mb-6">
        <Shield className="w-10 h-10 text-[#0072BC] mx-auto mb-2" />
        <h3 className="text-xl font-semibold text-white">Stability Assessment</h3>
        <p className="text-sm text-slate-400 mt-1">Evaluate whether the assistance will truly stabilize the situation</p>
      </div>
      <div className="space-y-4">
        {STABILITY_CHECKS.map(check => (
          <div key={check.id} className="p-4 bg-white/5 border border-white/10 rounded-xl" data-testid={`stability-${check.id}`}>
            <p className="text-white text-sm mb-3">{check.label}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setStabilityChecks({...stabilityChecks, [check.id]: true})}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  stabilityChecks[check.id] === true
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                    : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                Yes
              </button>
              <button
                onClick={() => setStabilityChecks({...stabilityChecks, [check.id]: false})}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  stabilityChecks[check.id] === false
                    ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                    : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                No
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-center">
        <Button
          onClick={handleStabilitySubmit}
          className="bg-[#0072BC] hover:bg-[#005a96] px-8"
          data-testid="stability-continue-btn"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // Render Pastoral Discernment (Section 5)
  const renderPastoralReview = () => (
    <div data-testid="pastoral-review">
      <div className="text-center mb-6">
        <Heart className="w-10 h-10 text-[#0072BC] mx-auto mb-2" />
        <h3 className="text-xl font-semibold text-white">Pastoral Discernment Team Review</h3>
        <p className="text-sm text-slate-400 mt-1">Final review for effectiveness, compassion, stewardship, and mission alignment</p>
      </div>

      <div className="space-y-3 mb-6">
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <p className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wider">Effectiveness</p>
          <p className="text-sm text-slate-300">Will this truly solve the problem or only provide temporary relief?</p>
        </div>
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <p className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wider">Compassion</p>
          <p className="text-sm text-slate-300">Is the human dignity of the individual/household respected?</p>
        </div>
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <p className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wider">Stewardship</p>
          <p className="text-sm text-slate-300">Is this a sustainable solution? Will it prevent a recurring need?</p>
        </div>
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
          <p className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wider">Mission Alignment</p>
          <p className="text-sm text-slate-300">Does this support SVDP's goals — preventing homelessness and upholding dignity?</p>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-300 mb-1">Review Notes (Optional)</label>
        <textarea
          value={pastoralNotes}
          onChange={(e) => setPastoralNotes(e.target.value)}
          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] outline-none resize-none"
          rows={3}
          placeholder="Additional notes from pastoral review..."
          data-testid="pastoral-notes"
        />
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={() => handlePastoralDecision('approve')}
          className="flex-1 max-w-[200px] py-4 px-6 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 font-semibold rounded-xl transition-colors border border-emerald-500/30"
          data-testid="pastoral-approve-btn"
        >
          Approve
        </button>
        <button
          onClick={() => handlePastoralDecision('refer')}
          className="flex-1 max-w-[200px] py-4 px-6 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 font-semibold rounded-xl transition-colors border border-amber-500/30"
          data-testid="pastoral-refer-btn"
        >
          Refer for Support
        </button>
      </div>
    </div>
  );

  // Render the outcome screen
  const renderOutcome = () => (
    <div className={`text-center p-8 rounded-xl border-2 ${getOutcomeColor(outcome.type)}`} data-testid="decision-tree-outcome">
      <div className="flex justify-center mb-4">
        {getOutcomeIcon(outcome.type)}
      </div>
      <h3 className="text-2xl font-bold text-white mb-2">
        {getOutcomeTitle(outcome.type)}
      </h3>
      <p className="text-slate-300 text-lg mb-6">
        {outcome.message}
      </p>

      {/* Summary */}
      <div className="text-left bg-white/5 rounded-xl p-4 mt-4 border border-white/10">
        <h4 className="font-semibold mb-3 text-slate-300 text-sm uppercase tracking-wider">Assessment Summary</h4>
        <div className="space-y-1.5 text-sm">
          {Object.entries(responses).map(([key, value]) => {
            if (key === 'household_data' || key === 'stability_checks' || key === 'pastoral_review') return null;
            const allQuestions = [...ELIGIBILITY_QUESTIONS, ...HOUSING_QUESTIONS];
            const q = allQuestions.find(q => q.id === key);
            if (!q) return null;
            return (
              <div key={key} className="flex items-start gap-2">
                <span className={`font-medium ${value === 'yes' ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {value === 'yes' ? '\u2713' : '\u2717'}
                </span>
                <span className="text-slate-400">{q.question}</span>
              </div>
            );
          })}
          {responses.household_data && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <p className="text-slate-400">Household: {responses.household_data.total_household_size || 'N/A'} members, {responses.household_data.marital_status || 'N/A'}, {responses.household_data.number_of_income_sources || 'N/A'} income source(s)</p>
            </div>
          )}
          {responses.stability_checks && (
            <div className="mt-2 pt-2 border-t border-white/10">
              {STABILITY_CHECKS.map(check => (
                <div key={check.id} className="flex items-start gap-2">
                  <span className={`font-medium ${responses.stability_checks[check.id] ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {responses.stability_checks[check.id] ? '\u2713' : '\u2717'}
                  </span>
                  <span className="text-slate-400">{check.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render current content based on section
  const renderContent = () => {
    if (outcome) return renderOutcome();

    switch (currentSection) {
      case 1:
        return renderYesNoQuestion(ELIGIBILITY_QUESTIONS[currentQuestionIndex], ELIGIBILITY_QUESTIONS);
      case 2:
        return renderHouseholdForm();
      case 3:
        return renderYesNoQuestion(HOUSING_QUESTIONS[currentQuestionIndex], HOUSING_QUESTIONS);
      case 4:
        return renderStabilityAssessment();
      case 5:
        return renderPastoralReview();
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900/95 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="decision-tree-wizard">
        {/* Header */}
        <div className="bg-[#0072BC]/10 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Rental Assistance Decision Tree</h2>
            <p className="text-sm text-slate-400">{neighbor?.first_name} {neighbor?.last_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors" data-testid="decision-tree-close-btn">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Section Progress */}
        {!outcome && renderSectionProgress()}

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {renderContent()}

          {/* Volunteer Guidance Panel */}
          {!outcome && VOLUNTEER_GUIDANCE[currentSection] && (
            <div className="mt-6" data-testid="volunteer-guidance-panel">
              <button
                onClick={() => setShowGuidance(!showGuidance)}
                className="w-full flex items-center justify-between px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-300 hover:bg-amber-500/15 transition-colors"
                data-testid="toggle-guidance-btn"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Lightbulb className="w-4 h-4" />
                  Volunteer Guidance: {VOLUNTEER_GUIDANCE[currentSection].title}
                </span>
                {showGuidance ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showGuidance && (
                <div className="mt-2 px-4 py-3 bg-amber-500/5 border border-amber-500/10 rounded-xl space-y-2">
                  {VOLUNTEER_GUIDANCE[currentSection].tips.map((tip, idx) => (
                    <p key={idx} className="text-sm text-slate-400 flex items-start gap-2">
                      <span className="text-amber-400/70 mt-0.5 shrink-0">&bull;</span>
                      <span>{tip}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 px-6 py-4 bg-white/5 flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={history.length === 0 && !outcome}
            className="border-white/20 text-slate-300 hover:bg-white/10"
            data-testid="decision-tree-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {outcome ? (
            <Button onClick={handleSave} disabled={saving} className="bg-[#0072BC] hover:bg-[#005a96]" data-testid="decision-tree-save-btn">
              {saving ? 'Saving...' : 'Save & Close'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={onClose} className="border-white/20 text-slate-300 hover:bg-white/10" data-testid="decision-tree-cancel-btn">
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecisionTreeWizard;
